/**
 * A filter control: a button that states its current value and opens a small
 * panel under it. Closes on Escape and on a click elsewhere.
 */
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

type PopoverProps = {
  naam: string;
  /** The selection in words; absent when the control narrows nothing down. */
  samenvatting?: string;
  breedte?: number;
  children: ReactNode;
};

export function Popover({ naam, samenvatting, breedte = 240, children }: PopoverProps) {
  const [open, zetOpen] = useState(false);
  const houder = useRef<HTMLDivElement>(null);
  const id = useId();
  const actief = samenvatting !== undefined;

  useEffect(() => {
    if (!open) return;
    function bij(event: MouseEvent) {
      if (!houder.current?.contains(event.target as Node)) zetOpen(false);
    }
    function toets(event: KeyboardEvent) {
      if (event.key === 'Escape') zetOpen(false);
    }
    document.addEventListener('mousedown', bij);
    document.addEventListener('keydown', toets);
    return () => {
      document.removeEventListener('mousedown', bij);
      document.removeEventListener('keydown', toets);
    };
  }, [open]);

  return (
    <div ref={houder} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => zetOpen((vorige) => !vorige)}
        data-actief={actief}
        className="keuzeknop"
      >
        <span>{naam}</span>
        {actief ? <span className="cijfers font-medium">{samenvatting}</span> : null}
        <ChevronDown className="h-3 w-3 text-faint" aria-hidden="true" />
      </button>

      {open ? (
        <div
          id={id}
          style={{ width: breedte }}
          className="paneel absolute top-[40px] left-0 z-30 p-2.5"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
